import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { TemplateType } from 'constants/templateTypes';
import { AppPlatform } from 'modules/mobileApps/types';
import { Moment } from 'moment';
import { DateType } from 'ui/datePicker';
import { ColumnData as CD } from 'ui/tableHeader';
import { Segment } from './components/Audience/types';

type MediumMetricsKey = 'City Data' | 'Device Data' | string;

export enum Medium {
  EMAIL = 'Email',
  PUSH = 'Push',
  SMS = 'SMS',
  IN_APP = 'InApp',
  WEB_PUSH = 'WebPush',
}

export enum CampaignStates {
  RUNNING = 'Running',
  FINISHED = 'Finished',
  ABORTED = 'Aborted',
}

export enum CampaignTypes {
  BLAST = 'Blast',
  TRIGGERED = 'Triggered',
  WITH_AI = 'WithAI',
  FROM_WORKFLOW = 'FromWorkflow',
  WITH_EXPERIMENT = 'WithExperiment',
  ELIGIBILITY = 'Eligibility',
}

export type AggMediumMetrics = {
  [key: MediumMetricsKey]: {
    value: number | { [cityOrDevice: string]: number };
    histogram?: {
      buckets: { time: number; value: number }[];
    };
    destinations?: InAppDestination[];
  };
};

export type AggMetricDataByMedium = {
  [medium: string]: {
    value: number;
    histogram?: {
      buckets: { time: number; value: number }[];
    };
  };
};

export type AggOverallMetrics = {
  [metric: string]: {
    value: number;
    mediums: AggMetricDataByMedium;
    histogram: {
      buckets: { time: number; value: number }[];
    };
  };
};

export type AggMetricResponse = {
  mediumMetrics: AggMediumMetrics;
  overallMetrics: AggOverallMetrics;
};

export type TimeSeriesData = AggMetricResponse;

export type Campaign = {
  campaignId: number;
  campaignName: string;
  campaignState: string;
  campaignType: string;
  launchDate?: string;
  medium: string;
  templateId?: number;
  metrics?: Record<string, any>;
};

export type DestinationsObj = {
  destinations?: InAppDestination[];
};

export type CampaignLevelMetrics = {
  campaignId: number;
  mediumMetrics: {
    [metricName: string]: {
      value: number | DestinationsObj;
    };
  };
  overallMetrics: {
    [metricName: string]: { value: number };
  };
}[];

export type ParsedOverallMetric = { enumName: string };
export type ParsedMediumMetric = { name: string };

export type InAppDestination = {
  appPackageName: string;
  platform: AppPlatform;
  appName: string;
  value: string;
};

export type CampaignFilterOption = {
  id: string;
  value: number;
  label: string;
  doubleLine?: string;
  categoryId?: string;
  uniqueId?: string;
};

export enum CampaignFiltersKeys {
  STATES = 'states',
  TYPES = 'types',
  CAMPAIGN_IDS = 'campaignIds',
  MESSAGE_TYPE_IDS = 'messageTypeIds',
  WORKFLOW_IDS = 'workflowIds',
  LABEL_IDS = 'labelIds',
  TEMPLATE_TYPES = 'templateTypes',
}

export type CampaignFilters = {
  [CampaignFiltersKeys.STATES]?: CampaignStates[];
  [CampaignFiltersKeys.TYPES]?: CampaignTypes[];
  [CampaignFiltersKeys.CAMPAIGN_IDS]?: number[];
  [CampaignFiltersKeys.MESSAGE_TYPE_IDS]?: number[];
  [CampaignFiltersKeys.WORKFLOW_IDS]?: number[];
  [CampaignFiltersKeys.LABEL_IDS]?: number[];
  [CampaignFiltersKeys.TEMPLATE_TYPES]?: TemplateType[];
};

export type QueryParams = {
  messageMediums?: string[];
  endTime?: string;
  startTime?: string;
  timePreset?: string;
  timezone?: string;
  metrics?: string[];
  tab?: string;
  segment?: string;
  region?: string;
  overviewMetrics?: string[];
  segmentMetrics?: string[];
  timeSeriesMetrics?: string[];
  variantId?: number[];
};

export type QueryParamErrors = {
  invalidParamList: string[];
  invalidValsByParam: Record<string, string[]>;
};

export type DateParams = {
  startTime: DateType;
  endTime: DateType;
};

export type SetDateParams = (dateParams: DateParams) => void;

export type SetStateActionStr = React.Dispatch<React.SetStateAction<string>>;

export type SetStateActionStrArr = React.Dispatch<React.SetStateAction<string[]>>;

export type AllFilters = {
  selectedMediums: string[];
  selectedCrossMetrics: string[];
  timePreset: string;
  timezone: string;
  dateParams: DateParams;
  campaignFilters: CampaignFilters;
  selectedTab: string;
  selectedSegment: Segment;
  region: string[];
  campaignsSortDirection: string;
  campaignsSortedBy: string;
  audienceSortDirection: string;
  audienceSortedBy: string;
  snippetsSortDirection?: SORT_DIRECTIONS;
  snippetsSortedBy?: string;
};

export type SavedViewMetaData = {
  id: number;
  name: string;
  description?: string;
  isPrivate: boolean;
  defaultTab: string;
  isProjectDefault: boolean;
};

export type Preset = 'TODAY' | 'PAST_3_DAYS' | 'PAST_7_DAYS' | 'PAST_30_DAYS' | 'CUSTOM_RANGE';

export type IsoString = string | undefined;
export type IsoStringDateParams = {
  startTime: IsoString;
  endTime: IsoString;
};

export type SavedView = SavedViewMetaData & {
  createdAt: Moment;
  updatedAt: Moment;
  createdBy: number;
  updatedBy: number;
};

export type SavedViewSettings = {
  selectedMediums: string[];
  selectedMetrics: {
    overallMetrics: { enumName: string }[];
    mediumMetrics: { name: string }[];
  };
  timeFilters: {
    timePreset: string;
    timezone: string;
    startTime: IsoString;
    endTime: IsoString;
  };
  campaignFilters: CampaignFilters;
  tabOpts: {
    selectedSegment: Segment;
    region: string[];
    campaignsSortedBy: string;
    campaignsSortDirection: string;
    audienceSortedBy: string;
    audienceSortDirection: string;
    snippetsSortedBy?: string;
    snippetsSortDirection?: SORT_DIRECTIONS;
  };
};

export type SavedViewParams = SavedViewMetaData & {
  settings: SavedViewSettings;
};
export interface ColumnData extends CD {
  ChildrenCell?: ({ row }: { row: any }) => JSX.Element;
  RowCell?: ({ row }: { row: any }) => JSX.Element;
  childrenValue?: any;
}

export type CampaignData = {
  campaignId: number;
  campaignName: string;
  campaignState: string;
  campaignType: string;
  launchDate?: string;
  medium: string;
  templateId?: number;
  metrics?: Record<string, any>;
  breakdownMetrics?: Record<string, any>;
  childrenRows?: Record<string, any>[];
};

export type CSVHeader = {
  label: string;
  key: string;
};

export type SelectedMetrics = { name: string }[];

export type ReportEventLabel = {
  badgeText: string;
  value: string;
  id: string;
  label: string;
};

export type GqlMIWorkflowsQueryResponse = {
  workflows: {
    results: {
      id: number;
      name: string;
    }[];
  };
};

export type MessageType = {
  id: number;
  name: string;
  messageChannel: {
    id: number;
    name: string;
    channelType: string;
  };
};

export type GqlMIMessageTypesQueryResponse = {
  messageTypes: MessageType[];
};

export type GqlMICampaignsQueryResponse = {
  campaigns: {
    results: {
      id: number;
      name: string;
      campaignState: CampaignStates;
      campaignTypeDefinition: CampaignTypes;
    }[];
  };
};

export type GqlFetchCampaignLabelsResponse = {
  campaignLabels: {
    name: string;
    id: number;
  }[];
};

export type GqlFetchMessageMediumsResponse = {
  data?: {
    messageMediums?: string[];
  };
};

export type Metric = {
  metricName: string;
  metricNiceName: string;
  category: string | null;
  valueType: string;
  mediums: string[];
  metrics?: { metricName: string }[];
};

export type MetricOption = {
  label: string;
  categoryId?: string;
  id: string;
  value: string;
  doubleLine?: string;
};

export type RestQueryParams = Record<string, any>;

export enum RestDataIdentifier {
  DEVICE = 'DeviceData',
  LOCATION = 'CityData',
  TIME_SERIES = 'TimeSeries',
  CAMPAIGNS_FOR_METRICS = 'CampaignForMetrics',
  OVERVIEW = 'Overview',
  PDF_REPORT = 'PDFReport',
}

/** PDF Report Modal */
export enum ChartDisplay {
  ONE = '1',
  FOUR = '4',
}

export enum SegmentTimeFrequency {
  WEEKLY = '1w',
  MONTHLY = '1M',
  QUARTERLY = '1q',
}
